<template>
  <main role="main" class="premium-wrapper">
    <!-- Nền trắng tinh khôi/Xanh nhạt -->

    <!-- Loading state -->
    <div v-if="isLoading" class="loading-full">
      <div class="loader-content">
        <b-spinner variant="primary" style="width: 3rem; height: 3rem;" class="mb-3" />
        <p class="text-dark">{{ $t("loadingData") }}</p>
      </div>
    </div>

    <!-- Container chính -->
    <b-container
      v-if="meeting"
      class="d-flex flex-column justify-content-start align-items-center min-vh-100 py-5"
    >
      <div v-if="isActiveMeeting" class="modern-card-container">
        <div class="modern-white-card p-4 p-md-5">
          <!-- Header thông tin cuộc họp -->
          <div class="form-header mb-4 text-center">
            <h2 class="meeting-title-modern">{{ meeting.name }}</h2>

            <div class="d-flex flex-column align-items-center">
              <div class="d-flex flex-wrap align-items-center justify-content-center mt-1" style="gap: 8px;">
                <div v-if="meeting.id" class="meeting-id-modern">
                  ID: <span class="id-value">{{ meeting.id }}</span>
                </div>
                <div class="meeting-status-modern">
                  <span class="status-dot" :class="meeting.status.toLowerCase()"></span>
                  <span class="status-text">{{ statusText[meeting.status] }}</span>
                </div>
              </div>

              <div v-if="meeting.role === roleJoinMeeting.OPERATOR && meeting.status !== enumMeetingStatus.END" class="note-role mt-2">
                 <!-- <b-icon icon="shield-check" class="mr-1"></b-icon> -->
                 {{ $t("joinMeetingOperator")}} "{{ $t("roleAdmin") }}"
              </div>
            </div>
          </div>

          <!-- Form nhập liệu -->
          <b-form class="text-left modern-form" id="join-meeting-form" @submit.prevent="joinMeeting">
            <div class="form-body">
              <!-- Họ và tên -->
              <b-form-group label-for="input-full-name" class="modern-group">
                <template v-slot:label>
                  <strong>{{ $t("fullName") }}</strong> <span class="text-danger">*</span>
                </template>
                <b-form-input
                  id="input-full-name"
                  class="modern-input-light"
                  type="text"
                  required
                  minlength="2"
                  maxlength="500"
                  v-model="fullName"
                  :placeholder="$t('namePlaceHolder')"
                  :disabled="isSubmitting"
                  :class="{ 'input-disabled-light': isSubmitting }"
                  :pattern="fullNamePattern"
                  @invalid="$event.target.setCustomValidity($t('FormatInvalidName'))"
                  @input.native="$event.target.setCustomValidity('')"
                />
              </b-form-group>

              <!-- Tên tổ chức nội bộ -->
              <b-form-group
                v-if="shouldShowOrgField"
                label-for="input-org-name"
                class="modern-group"
              >
                <template v-slot:label>
                  <strong>{{ $t('workOrganizationName') }}</strong>
                </template>
                <b-form-input
                  id="input-org-name"
                  type="text"
                  class="modern-input-light"
                  maxlength="500"
                  v-model="orgName"
                  :disabled="isSubmitting"
                  :class="{ 'input-disabled-light': isSubmitting }"
                />
              </b-form-group>

              <!-- Mã truy cập (Nếu cần) -->
              <b-form-group label-for="input-access-code" class="modern-group mb-4">
                <template v-slot:label>
                  <strong>{{ $t("enterAccessCode") }}</strong> <span class="text-danger">*</span>
                </template>
                <b-form-input
                  id="input-access-code"
                  type="text"
                  class="modern-input-light"
                  required
                  minlength="2"
                  :placeholder="$t('accessCodePlaceHolder')"
                  maxlength="32"
                  v-model="accessCode"
                  :disabled="isSubmitting || (meeting && meeting.isReOpen)"
                  :class="{ 'input-disabled-light': isSubmitting || (meeting && meeting.isReOpen) }"
                />
              </b-form-group>
            </div>

            <!-- Thông báo lỗi form -->
            <transition name="fade">
              <div v-if="formErrorMsg" class="modern-error-light mb-4 text-center">
                <b-icon icon="exclamation-circle-fill" class="mr-2"></b-icon>
                {{ $t(formErrorMsg) }}
              </div>
            </transition>

            <div class="button-section">

              <!-- Tham gia trên trình duyệt/ Mở app -->
              <div v-if="shouldDisplayWebButton">
                <b-button
                  type="submit"
                  variant="primary"
                  class="btn-modern-primary w-100"
                >
                  <!-- <b-icon icon="globe" class="mr-2"></b-icon> -->
                  {{ buttonLabel() }}
                </b-button>
              </div>

              <!-- Giao diện Mobile/Tablet -->
              <div v-if="shouldDisplayMobileButtons">
                <b-button
                  variant="primary"
                  class="btn-modern-primary w-100 mb-3"
                  @click="openApp($event)"
                >
                  <b-icon icon="phone" class="mr-2"></b-icon>
                  {{ $t("joinMeeting") }}
                </b-button>

                <b-button
                  v-if="!isOpenApp"
                  variant="outline-primary"
                  class="btn-modern-outline w-100"
                  @click="joinMeeting()"
                >
                   <b-icon icon="globe" class="mr-2"></b-icon>
                  {{ $t("joinOnBrowser") }}
                </b-button>
              </div>

              <div class="terms-agreement-container mt-4">
                {{ $t("byJoiningMeetingYouAccept") }}
                <a href="https://vnptai.io/vi/dieukhoansudung" target="_blank" rel="noopener noreferrer" class="terms-link">{{ $t("signUpTermOfUse") }}</a>,
                <a href="https://vnptai.io/vi/baovedulieucanhan" target="_blank" rel="noopener noreferrer" class="terms-link">{{ $t("personalDataProtectionPolicy") }}</a>
                {{ $t("and") }}
                <a href="https://vnptai.io/vi/chinhsachbaomat" target="_blank" rel="noopener noreferrer" class="terms-link">{{ $t("signUpPrivacyPolicy") }}</a>
                {{ $t("productVnptGoMeet") }}
              </div>
            </div>
          </b-form>
        </div>
      </div>

      <!-- Giao diện khi cuộc họp đã kết thúc -->
      <div v-else class="modern-card-container">
        <div class="modern-white-card p-4 p-md-5 text-center">
          <h2 class="meeting-title-modern mb-4">{{ meeting.name }}</h2>
          <div class="d-flex flex-wrap align-items-center justify-content-center mb-2" style="gap: 8px;">
            <div v-if="meeting.id" class="meeting-id-modern">
              ID: <span class="id-value">{{ meeting.id }}</span>
            </div>
            <div class="meeting-status-modern">
               <span class="status-dot" :class="meeting.status.toLowerCase()"></span>
               <span class="status-text">{{ $t("meetingStatusEnded") }}</span>
            </div>
          </div>
          <!-- <div class="button-section mt-3">
            <b-button
              variant="primary"
              class="btn-modern-primary w-100 mb-3"
              @click="goToGm"
            >
              <b-icon icon="plus-circle" class="mr-2"></b-icon>
              {{ $t("joinWithAnotherCode") || "Tham gia cuộc họp khác" }}
            </b-button>
            <b-button
              variant="outline-primary"
              class="btn-modern-outline w-100"
              href="/"
            >
              <b-icon icon="house-door" class="mr-2"></b-icon>
              {{ $t("portalHome") || "Về trang chủ" }}
            </b-button>
          </div> -->
        </div>
      </div>

      <!-- Widget VNPT GoMeet -->
      <vnpt-gomeet
        v-if='sessionToken != null && sessionToken != "" && haveWidget'
        id="gomeet-widget-edge"
        :joinUrl="joinUrl"
        i-width-max="350" i-width-min="350" i-height-min="350" i-height-max="400"
      ></vnpt-gomeet>
    </b-container>

    <!-- Giao diện lỗi khi không thấy cuộc họp -->
    <b-container v-else class="min-vh-100 d-flex align-items-center justify-content-center">
      <div class="modern-white-card p-4 text-center" style="max-width: 400px;">
        <b-icon icon="exclamation-triangle" variant="warning" font-scale="3" class="mb-3"></b-icon>
        <p class="text-dark">{{ $t(formErrorMsg) }} {{ $t("MeetingNotFound") }}</p>
        <b-button variant="link" class="text-primary mt-2" @click="goToGm">
          {{ $t("joinWithAnotherCode") || "Tham gia bằng mã khác" }}
        </b-button>
      </div>
    </b-container>
  </main>
</template>

<style lang="scss" scoped>
.premium-wrapper {
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f4f8 0%, #e2ebf0 100%);
  &::before {
    content: '';
    position: absolute;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, rgba(255, 255, 255, 0) 70%);
    top: -200px;
    left: -200px;
    border-radius: 50%;
  }
}

.loading-full {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-content {
  text-align: center;
  animation: float 2s ease-in-out infinite;

  p {
    font-weight: 600;
    color: #475569;
    letter-spacing: 0.5px;
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.modern-card-container {
  width: 100%;
  max-width: 480px;
  position: relative;
  z-index: 10;
  animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.modern-white-card {
  background: #f3f9ff;
  backdrop-filter: blur(20px);
  border-radius: 28px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.6) inset;
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: #1e293b;
  width: 100%;
  overflow: hidden;
  transition: transform 0.3s ease;
}

.form-header {
  margin-bottom: 2.5rem;
}

.meeting-title-modern {
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 12px;
  letter-spacing: -0.5px;
  line-height: 1.3;
}

.meeting-status-modern {
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  padding: 6px 16px;
  border-radius: 9999px;
  border: 1px solid rgba(0,0,0,0.04);
  box-shadow: 0 2px 6px rgba(0,0,0,0.03);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  box-shadow: 0 0 8px currentColor;
  &.inprogress { background: #10b981; color: #10b981; }
  &.pending { background: #f59e0b; color: #f59e0b; }
  &.end { background: #64748b; color: #64748b; }
}

.status-text {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.meeting-id-modern {
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  padding: 6px 14px;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
  font-size: 13px;
  color: #64748b;
  font-weight: 500;

  .b-icon {
    font-size: 14px;
    color: #64748b;
    margin-right: 4px;
  }

  .id-value {
    font-size: 13px;
    color: #475569;
    font-weight: 600;
    margin-left: 2px;
    letter-spacing: 0.5px;
  }
}

.note-role {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  padding: 6px 14px;
  border-radius: 12px;
  margin-top: 16px;
}

.modern-group {
  margin-bottom: 24px;

  label {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    strong { font-weight: 600; }
  }
}

.modern-input-light {
  height: 52px !important;
  background: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid #cce3ff !important;
  border-radius: 14px !important;
  color: #0f172a !important;
  font-size: 18px !important;
  font-weight: 500 !important;
  // box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02), 0 0 0 1px #e2e8f0 inset !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

  &:focus {
    background: #fff !important;
    border-color: transparent !important;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15), 0 0 0 2px #3b82f6 inset !important;
    outline: none !important;
  }

  &::placeholder {
    color: #94a3b8 !important;
    font-weight: 400 !important;
  }
}

.input-disabled-light {
  background: #f8fafc !important;
  // box-shadow: 0 0 0 1px #e2e8f0 inset !important;
  color: #94a3b8 !important;
  cursor: not-allowed;
}

.modern-error-light {
  background: #fef2f2;
  color: #dc2626;
  padding: 14px 16px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  border: 1px solid #fee2e2;
  animation: slideInError 0.3s ease;
}

@keyframes slideInError {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}

.btn-modern-primary {
  height: 54px;
  border-radius: 14px;
  font-weight: 600;
  font-size: 16px;
  background: linear-gradient(135deg, #137ae1 0%, #004f9e 100%);
  border: none;
  color: #fff;
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.25), inset 0 1px 0 rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    // transform: translateY(-2px);
    box-shadow: 0 12px 25px rgba(37, 99, 235, 0.35), inset 0 1px 0 rgba(255,255,255,0.15);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 4px 10px rgba(37, 99, 235, 0.2);
  }

  &:disabled {
    background: #cbd5e0;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
}

.btn-modern-outline {
  height: 54px;
  border-radius: 14px;
  font-weight: 600;
  font-size: 16px;
  border: 2px solid #e2e8f0;
  color: #475569;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e0;
    color: #1e293b;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 576px) {
  .premium-wrapper::before {
    display: none;
  }
  .modern-card-container {
    max-width: 100%;
    margin: 0;
  }
  .modern-white-card {
    border-radius: 0;
    min-height: 100vh;
    padding: 40px 24px !important;
    box-shadow: none;
    border: none;
    background: #f3f9ff;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.button-section {
  margin-top: 32px;
}

.terms-agreement-container {
  font-size: 13px;
  line-height: 1.6;
  color: #64748b;
  text-align: center;
  // margin-bottom: 20px;
  font-weight: 400;
}

.terms-link {
  color: #137ae1;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease, text-decoration 0.2s ease;
  text-decoration: underline;
  &:hover {
    color: #004f9e;
    text-decoration: underline;
  }
}
</style>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import _ from "lodash";
import BaseComponent from "@/components/BaseComponent";

import * as meetingService from "@/service/meetingService";
import * as util from "@/service/util";
import UAParser from "ua-parser-js";
import { MeetingStatus } from '../enums/MeetingStatus';
import {enumErrorType} from "@/views/enums/ErrorType";
import {RoleJoinMeeting} from "@/views/enums/UserRole";
import { JoinWithNameParam } from "@/model/JoinWithNameParam";

@Component
export default class JoinMeeting extends BaseComponent {

  readonly statusText = {
    PENDING: this.$t("meetingStatusPending").toString(),
    INPROGRESS: this.$t("meetingStatusInProgress").toString(),
    END: this.$t("meetingStatusEnded").toString(),
    CANCEL: this.$t("meetingStatusCancelled").toString(),
  };

  readonly statusBadgeVariant = {
    PENDING: "warning",
    INPROGRESS: "success",
    END: "secondary",
    CANCEL: "danger"
  };

  meetingSecretKey: string = null;

  enumMeetingStatus = MeetingStatus;
  roleJoinMeeting = RoleJoinMeeting;

  isLoading = true;
  meeting = null;
  meetingId: number | string = null;
  errorMsg = "";

  accessCode = "";
  ver = "";

  shouldShowOrgField = false;
  fullName = "";
  orgName = "";
  paramEmail: string = '';

  formErrorMsg = "";
  isSubmitting = false;

  appDeepLink = "";
  appDownloadLink = "";
  isMobile = false;
  coreVersion = "";
  sessionToken = "";
  joinUrl = "";
  haveWidget = false;
  /**
   * Kiểm tra cuộc họp có đang hoạt động không
   * (Không phải trạng thái END hoặc CANCEL)
   * Đặc biệt: Với trạng thái cuộc họp là END (đã kết thúc) nhưng người dùng là
   * OPERATOR (Quản trị) và cuộc họp được cấu hình cho phép mở lại (isReOpen = true),
   * vẫn coi là hoạt động để hiển thị form tham gia / mở lại.
   */
  get isActiveMeeting(): boolean {
    if (this.meeting?.status === this.enumMeetingStatus.END && this.meeting?.role === this.roleJoinMeeting.OPERATOR && this.meeting?.isReOpen) {
      return true;
    }
    return ![this.enumMeetingStatus.END, this.enumMeetingStatus.CANCEL].includes(
      this.meeting?.status as MeetingStatus
    );
  }

  /**
   * Kiểm tra có nên hiển thị nút join trên web không
   * Hiển thị khi: không có appDeepLink HOẶC (không phải mobile VÀ có appDeepLink)
   * Đặc biệt: Đối với luồng mở lại cuộc họp của OPERATOR, luôn bắt buộc hiển thị
   * nút Web (Mở lại cuộc họp) và không đi qua luồng mở ứng dụng di động/desktop.
   */
  get shouldDisplayWebButton(): boolean {
    if (this.meeting?.status === this.enumMeetingStatus.END && this.meeting?.role === this.roleJoinMeeting.OPERATOR && this.meeting?.isReOpen) {
      return true;
    }
    return !this.appDeepLink || (!this.isMobile && !!this.appDeepLink);
  }

  /**
   * Kiểm tra có nên hiển thị nút join mobile không
   * Hiển thị khi: là mobile VÀ có appDeepLink
   * Đặc biệt: Ẩn nút mobile khi OPERATOR đang mở lại cuộc họp đã kết thúc.
   */
  get shouldDisplayMobileButtons(): boolean {
    if (this.meeting?.status === this.enumMeetingStatus.END && this.meeting?.role === this.roleJoinMeeting.OPERATOR && this.meeting?.isReOpen) {
      return false;
    }
    return this.isMobile && !!this.appDeepLink;
  }

  ///
  isOpenApp = false;

  /// APP
  readonly mobile = "https://gomeet01.vnpt.vn/storage";
  readonly android = "https://play.google.com/store/apps/details?id=com.vnpt.gomeetv3";
  readonly ios = "https://apps.apple.com/vn/app/vnpt-gomeet-v3/id1662423818";
  readonly macos = "https://gomeet01.vnpt.vn/storage/desktop/mac/gomeet.dmg";
  readonly windows = "https://gomeet01.vnpt.vn/storage/desktop/win/gomeet.exe";
  readonly linux = null;

  // Watch: recompute deep link mỗi khi người dùng chỉnh tên/tổ chức/mã truy cập
  // để app desktop/mobile luôn nhận đúng giá trị mới nhất (giống luồng người tham dự)
  @Watch('accessCode')
  @Watch('fullName')
  @Watch('orgName')
  onFormDataChange() {
    this.handleV4Deeplink();
  }

  async mounted() {
    this.meetingSecretKey = decodeURIComponent(this.$route.params.meetingSecretKey);
    this.accessCode = (this.$route.query.accessCode || this.$route.query.adminCode)?.toString().trim() || "";
    this.accessCode = this.accessCode.split(' ')[0].trim();

    this.ver = this.$route.query.v?.toString().trim() || "";
    this.paramEmail = decodeURIComponent(this.$route.query.email as string || '');

    this.shouldShowOrgField = this.$route.meta?.mayHaveOrgField && window["vnptEConferencePortalJoinMeetingWithOrgName"];

    const uap = new UAParser();
    const osname = uap.getOS().name.toLowerCase();
    if(osname == "mac os" && uap.getDevice().type == "tablet") {
      this.isMobile = true;
    }
    else {
      this.isMobile = ["android", "ios"].includes(osname);
    }

    await this.fetchMeeting();
  }

  unmounted() {
    var evt = new CustomEvent("versionChange", { detail: { type: null } });
      window.dispatchEvent(evt);
  }

  async fetchMeeting() {
    try {
      this.isLoading = true;
      this.meeting = await meetingService.getBySecretKey(this.meetingSecretKey, this.accessCode);
      this.meetingId = this.meeting?.id || null;
      this.coreVersion = this.meeting.version;
      this.fullName = this.getCookie('p_join_name') || this.meeting.joinName;
      var evt = new CustomEvent("versionChange", { detail: { type: this.coreVersion } });
      window.dispatchEvent(evt);
      this.handleV4Deeplink();
    } catch (e) {
      this.formErrorMsg = this.showError(e,enumErrorType.MESSAGE);
    } finally {
      this.isLoading = false;
    }
  }

  handleV4Deeplink() {
    const uap = new UAParser();
    const osname = uap.getOS().name.toLowerCase();
    let isMobile = false;
    if (["mac os", "macos", "mac"].includes(osname) && uap.getDevice().type != "tablet") {
      this.appDownloadLink = this.macos;
    } else if (osname.includes("windows")) {
      this.appDownloadLink = this.windows;
    } else if (osname === "android") {
      this.appDownloadLink = this.android;
      isMobile = true;
    } else if (osname === "ios" || ["mac os", "macos", "mac"].includes(osname) && uap.getDevice().type == "tablet") {
      this.appDownloadLink = this.ios;
      isMobile = true;
    } else if (navigator.userAgent.toLowerCase().includes("linux")) {
      this.appDownloadLink = this.linux;
    }

    if (this.appDownloadLink) {
      if (!document.head.querySelector(`meta[name="viewport"]`)) {
        const metaTag = document.createElement('meta');
        metaTag.name = "viewport";
        metaTag.content = "width=device-width,initial-scale=1.0";
        document.head.appendChild(metaTag);
      }

      var usp = new URLSearchParams({
        roomId: this.meetingSecretKey,
        accessCode: this.accessCode,
        name: util.joinStrings([this.fullName, this.orgName], " ")
      });

      if(this.ver != ""){
        usp = new URLSearchParams({
          roomId: this.meetingSecretKey,
          accessCode: this.accessCode,
          v: this.ver,
          name: util.joinStrings([this.fullName, this.orgName], " ")
        });
      }
      this.appDeepLink = `gomeetv4://${location.host}?${usp.toString()}`;
      if(isMobile) {
        this.appDeepLink = `gomeetv3://${location.host}?${usp.toString()}`;
      }
    }
  }

  async submitForm() {
    try {
      this.formErrorMsg = null;

      let param: JoinWithNameParam = {
        meetingSecretKey: this.meetingSecretKey,
        fullName: this.fullName.trim() || undefined,
        org: this.orgName.trim() || undefined,
        accessCode: this.accessCode,
      };

      // Thêm email nếu hợp lệ (giống luồng người tham dự)
      if (util.isValidEmail(this.paramEmail)) {
        param.email = this.paramEmail;
      }

      // Secret key > 32 ký tự: dùng joinBySecretKey (link đầy đủ)
      // Secret key <= 32 ký tự: dùng joinWithName (mã ngắn)
      let res;
      if (this.meetingSecretKey.length > 32) {
        res = await meetingService.joinBySecretKey(param);
      } else {
        res = await meetingService.joinWithName(param);
      }

      this.saveCookie('p_join_name', this.fullName.trim(), 365);

      this.sessionToken = res.sessionToken;
      if (this.sessionToken) {
        this.joinUrl = res.joinUrl;
        if(!this.coreVersion) {
          this.coreVersion = 'v3';
        }
        this.appDeepLink = window["vnptEConferencePortalV3AppDeepLinkPrefix"] + this.sessionToken;
        this.handleV4Deeplink();

      } else {
        location.href = res.joinUrl;
      }
    } catch (e) {
        const nameErrorMsg = e?.response?.data?.message?.value
                            ?? e?.response?.data?.message
                            ?? "Đã xảy ra lỗi, vui lòng kiểm tra thông tin và thử lại";
        this.formErrorMsg = this.$t(nameErrorMsg).toString();
    }
  }
  /**
   * Tham gia cuộc họp
   * Validate các trường bắt buộc trước khi submit - cần thiết vì nút "joinOnBrowser"
   * (hiển thị trên mobile) gọi trực tiếp qua @click, không đi qua validation HTML5 của form
   */
  async joinMeeting() {
    this.formErrorMsg = null;

    if (!this.meetingSecretKey || this.meetingSecretKey.trim().length < 2) {
      this.formErrorMsg = this.$t("joinCodeRequired") as string;
      return;
    }

    if (!this.fullName || !this.fullName.trim()) {
      this.formErrorMsg = this.$t("FullNameRequired") as string;
      return;
    }

    if (!this.accessCode || this.accessCode.trim().length < 2) {
      this.formErrorMsg = this.$t("AccessCodeRequired") as string;
      return;
    }

    try {
      this.isSubmitting = true;
      const res = await this.submitForm();
      if (this.joinUrl) {
        window.location.href = this.joinUrl;
      }
    } catch (e) {
      this.formErrorMsg = this.showError(e,enumErrorType.MESSAGE).toString();
    } finally {
      this.isSubmitting = false;
    }
  }
  /**
   * Xác định nhãn hiển thị cho nút tham gia/bắt đầu cuộc họp.
   * - Nếu là OPERATOR và cuộc họp chưa bắt đầu (PENDING): hiển thị "Bắt đầu cuộc họp".
   * - Nếu là OPERATOR và cuộc họp đã kết thúc (END) nhưng được phép mở lại: hiển thị "Mở lại cuộc họp".
   * - Ngược lại: hiển thị "Tham gia".
   */
  buttonLabel() {
    if (this.meeting?.role === this.roleJoinMeeting.OPERATOR) {
      if (this.meeting?.status === this.enumMeetingStatus.PENDING) {
        return this.$t("meetingStart");
      }
      if (this.meeting?.status === this.enumMeetingStatus.END && this.meeting?.isReOpen) {
        return this.$t("meetingReopen");
      }
    }
    return this.$t("joinMeetingBrowser");
  }

  /// Mở ứng dụng desktop app hoặc mobile app

  openApp(event) {

    ///
    if (this.isOpenApp) {
      return;
    }

    ///
    this.isOpenApp = true;

    ///
    event.preventDefault();

    ///
    window.location.href = this.appDeepLink;

    ///
    const now = Date.now();
    setTimeout(() => {
      if (Date.now() - now < 3500) {
        this.isOpenApp = false;
        // Nếu sau 2 giây mà app chưa mở thì chuyển qua store
        window.location.href = this.appDownloadLink;
      }
    }, 3000);
  }

  /**
   * Điều hướng về trang nhập mã tham gia (/gm)
   */
  goToGm() {
    this.$router.push('/gm').catch(() => {}).finally(() => {
       window.location.reload();
    });
  }

  // ==================== COOKIE METHODS ====================

  /**
   * Lưu cookie với thời gian hết hạn
   */
  saveCookie(name: string, value: string, days: number) {
    let expires = "";

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days*24*60*60*1000));
      expires = "; expires=" + date.toUTCString();
    }

    document.cookie = name + "=" + (value || "")  + expires + "; path=/";
  }

  /**
   * Đọc giá trị cookie theo tên
   */
  getCookie(name: string) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');

    for(let i=0;i < ca.length;i++) {
      let c = ca[i];
      while (c.charAt(0)==' ') c = c.substring(1,c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
    }

    return null;
  }
}
</script>
